'use client';

import { type FC, useId } from 'react';
import { Box, type BoxProps, List, ListItem, type ListItemProps, type ListProps } from '@mui/material';
import { Tab, type TabProps } from './Tab';
type Props<T extends object, P extends object> = {
  labelProps: T[];
  LabelComponent: FC<T>;
  contentProps: P[];
  ContentComponent: FC<P>;
  containerProps?: BoxProps;
  menuProps?: BoxProps;
  tabListProps?: ListProps;
  listItemProps?: ListItemProps;
  tabProps?: Omit<TabProps, 'htmlFor' | 'id'>;
  contentContainerProps?: BoxProps;
};
export const VerticalTabs = <T extends object, P extends object>({
  labelProps,
  LabelComponent,
  contentProps,
  ContentComponent,
  containerProps = {},
  menuProps = {},
  tabListProps = {},
  listItemProps = {},
  tabProps = {},
  contentContainerProps = {}
}: Props<T, P>) => {
  const id = useId();
  const {
    sx: containerSx,
    ...containerRest
  } = containerProps;
  const {
    sx: menuSx,
    ...menuRest
  } = menuProps;
  const {
    sx: tabListSx,
    ...tabListRest
  } = tabListProps;
  const {
    sx: listItemSx,
    ...listItemRest
  } = listItemProps;
  const {
    sx: contentContainerSx,
    ...contentContainerRest
  } = contentContainerProps;
  if (contentProps.length !== labelProps.length) {
    throw new Error('There should be the same number of labels and content');
  }
  return <Box sx={{
    display: {
      xs: 'none',
      sm: 'flex'
    },
    gap: 4,
    ...containerSx
  }} {...containerRest} id={id}>
      <Box sx={{
      display: 'none',
      visibility: 'hidden'
    }}>
        {labelProps.map((_, index) => {
        const tabId = `${id}-${index}`;
        return <Box key={`${tabId}-nav-input`} component="input" type="radio" name={`${id}-nav`} id={`${tabId}-nav-radio`} defaultChecked={index === 0} aria-controls={`${tabId}-nav-panel`} />;
      })}
      </Box>

      <Box sx={{
      paddingInlineStart: 0,
      marginY: 0,
      ...menuSx
    }} {...menuRest}>
        <List role="tablist" sx={{
        paddingY: 3,
        ...tabListSx
      }} {...tabListRest}>
          {labelProps.map((props, index_0) => {
          const tabId_0 = `${id}-${index_0}`;
          return <ListItem key={`${tabId_0}-list-item`} id={`${tabId_0}-nav-label`} sx={{
            width: '300px',
            padding: 0,
            ...listItemSx
          }} role="tab" aria-controls={`${tabId_0}-nav-panel`} {...listItemRest}>
                <Tab htmlFor={`${tabId_0}-nav-radio`} id={id} {...tabProps}>
                  <LabelComponent {...props} />
                </Tab>
              </ListItem>;
        })}
        </List>
      </Box>

      {contentProps.map((props_0, index_1) => {
      const tabId_1 = `${id}-${index_1}`;
      return <Box key={`${tabId_1}-content`} role="tabpanel" id={`${tabId_1}-nav-panel`} aria-labelledby={`${tabId_1}-nav-label`} sx={{
        display: 'none',
        [`#${id}:has(input#${tabId_1}-nav-radio:checked) &`]: {
          display: 'block'
        },
        ...contentContainerSx
      }} {...contentContainerRest}>
            <ContentComponent {...props_0} />
          </Box>;
    })}
    </Box>;
};